import styled from '@emotion/styled';
import type { CheckEventStarterKeyResponse } from '@racemap/sdk/schema/eventStarterKey';
import { HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import { DeviceClasses } from '@racemap/utilities/consts/events';
import { parseDateTimeSafe } from '@racemap/utilities/functions/datetime';
import { uuid4 } from '@racemap/utilities/functions/utils';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { Point } from '@racemap/utilities/types/types';
import { Alert, Button, Collapse, Divider, Form, Input, Space, Typography } from 'antd';
import sortBy from 'lodash/sortBy';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import {
  IconArrowDown,
  IconArrowRight,
  IconClose,
  IconExternal,
  IconGear,
  IconSetting,
} from 'src/components/BasicComponents/Icons';
import { getLeaderboardUrl } from 'src/lib/getLeaderboardUrl';
import { toast } from 'src/lib/toast';
import { HelperModal } from '../../components/Modal';
import { TrackDropView } from '../../components/TrackDropView';
import { Tristate } from '../../components/utils/tristateTask';
import { useAsyncAction } from '../../components/utils/useAsyncAction';
import YoutubeIframe from '../../components/YoutubeIframe';

const apiClient = RacemapAPIClient.fromWindowLocation();
const { Paragraph, Text, Title } = Typography;

type Track = { points: Array<Point>; name: string };
type UploadFormValues = {
  key: string;
  eventName: string;
  startNumber: string;
  name: string;
  track: Track | null;
};

export function UploadLandingView() {
  const [form] = Form.useForm<UploadFormValues>();
  const [shouldOpenPlayer, setShouldOpenPlayer] = useState(false);
  const [keyData, setKeyData] = useState<CheckEventStarterKeyResponse>({
    name: '',
    startNumber: '',
    eventId: '',
    eventName: '',
    starterId: null,
    keyUsed: false,
  });
  const [eventData, setEventData] = useState<RacemapEvent | null>(null);
  // HTTP status of a failed event load (404 = does not exist, 403 = not readable),
  // or null when the resolved event loaded fine.
  const [eventErrorStatus, setEventErrorStatus] = useState<number | null>(null);
  const track = Form.useWatch('track', form);
  const { status, run } = useAsyncAction();
  const endTime = parseDateTimeSafe(eventData?.endTime)?.toMillis() || Number.POSITIVE_INFINITY;
  const dateCheck =
    Date.now() < endTime ||
    eventData?.modules?.timing.visibleElements.participateAfterEvent ||
    eventData == null;
  const location = useLocation();

  useEffect(() => {
    if (location.search === '') return;
    const urlParams = new URLSearchParams(location.search);
    const matchEnteredKey = urlParams.get('key')?.match(/^([a-zA-Z0-9]{6})$/);
    form.setFieldsValue({
      ...(matchEnteredKey != null ? { key: matchEnteredKey[1].toUpperCase() } : {}),
      ...(urlParams.get('startNumber') != null
        ? { startNumber: urlParams.get('startNumber') ?? '' }
        : {}),
      ...(urlParams.get('name') != null ? { name: urlParams.get('name') ?? '' } : {}),
    });
    if (matchEnteredKey != null) form.validateFields(['key']);
  }, [location.search, form]);

  const metaDataTrack: { pointsOutsideEvent: Array<Point>; firstTime: Date } | null =
    useMemo(() => {
      if (track == null) return null;
      if (eventData == null)
        return {
          pointsOutsideEvent: track.points,
          firstTime: track.points[0].time,
        };

      const startTime = Date.parse(eventData.startTime);
      const endTime = Date.parse(eventData.endTime);
      return {
        pointsOutsideEvent: track.points.filter((p) => {
          return p.time.getTime() < startTime || p.time.getTime() > endTime;
        }),
        firstTime: track.points[0].time,
      };
    }, [track, eventData]);

  // Validates the key against the API and, on success, prefills the event details.
  // Thrown messages surface as antd field validation errors.
  async function validateKey(key: string) {
    if (key.length < 6) throw new Error('Key is too short.');
    let info: CheckEventStarterKeyResponse;
    try {
      info = await apiClient.checkEventStarterKey(key, { usedOk: true });
    } catch (err) {
      // Reset form and state on error
      setEventData(null);
      setKeyData({
        name: '',
        startNumber: '',
        eventId: '',
        eventName: '',
        starterId: null,
        keyUsed: false,
      });
      form.setFieldsValue({ eventName: '' });

      if (err instanceof HTTPFetchError && err.serverResponse.status === 403) {
        throw new Error('Key has been used.');
      }
      if (err instanceof HTTPFetchError && err.serverResponse.status === 404) {
        throw new Error('Key is invalid.');
      }
      throw new Error("Key couldn't be validated.");
    }
    if (info.eventId !== '') {
      try {
        setEventData(await apiClient.getEvent(info.eventId));
      } catch (err) {
        // The event resolved by the key can't be loaded: 404 means it no longer
        // exists, 403 means an anonymous key holder may not read it (draft or
        // archived). Either way uploads are not offered, so block the upload and
        // surface the matching error instead of letting it proceed.
        if (err instanceof HTTPFetchError && (err.status === 403 || err.status === 404)) {
          setEventData(null);
          setEventErrorStatus(err.status);
        } else {
          throw err;
        }
      }
    }
    setKeyData(info);
    form.setFieldsValue({
      eventName: info.eventName,
      ...(info.name != null ? { name: info.name } : {}),
      ...(info.startNumber != null ? { startNumber: info.startNumber } : {}),
    });
  }

  async function handleUpload(values: UploadFormValues) {
    if (!dateCheck) return;
    const key = values.key.toUpperCase();
    try {
      let appId = null;
      let starter = null;

      // Re-fetch instead of reading keyData state: state is set by the async
      // key validator and may be stale if the user submits before it commits.
      const freshKeyData = await apiClient.checkEventStarterKey(key, { usedOk: true });

      if (freshKeyData.keyUsed && freshKeyData.starterId != null) {
        starter = await apiClient.getStarter(freshKeyData.starterId, { key });
        if (starter.appId == null || starter.appId === '') {
          appId = uuid4();
          await apiClient.linkEventStarterKey(key, appId, {});
        } else {
          appId = starter.appId;
        }
      } else {
        appId = uuid4();

        // Create starter and attach to event through key
        starter = await apiClient.linkEventStarterKey(key, appId, {
          deviceClass: DeviceClasses.Upload,
          deviceType: '',
          name: values.name || undefined,
          startNumber: values.startNumber || undefined,
        });
      }

      // update startTime of the participant
      if (
        starter != null &&
        metaDataTrack?.firstTime != null &&
        (starter.startTime == null ||
          metaDataTrack.firstTime.getTime() < Date.parse(starter.startTime))
      ) {
        await apiClient.updateStarter(
          starter.id,
          {
            startTime: metaDataTrack?.firstTime.toISOString(),
          },
          { query: { appId } },
        );
      }

      // Upload points
      if (values.track?.points != null) {
        await apiClient.addPoints({ appId, points: values.track.points });
      }

      toast.success('Track successfully uploaded. Check out your track in the Tracking Map.');
      setShouldOpenPlayer(true);
    } catch (err: any) {
      toast.error("Couldn't upload track");
      console.error(err.message || 'Unknown error');
      throw err;
    }
  }

  function UploadHelperModal() {
    return (
      <HelperModal title="How to get your Activity" size="lg">
        <Collapse
          accordion
          items={[
            {
              key: '0',
              label: <Text strong>Garmin</Text>,
              children: (
                <>
                  <InstructionList>
                    <li>
                      Log in to{' '}
                      <a
                        href="https://connect.garmin.com"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Garmin Connect
                      </a>{' '}
                      on your computer.
                    </li>
                    <li>
                      Click on the link &quot;Activities&quot; <IconArrowRight /> &quot;All
                      Activities&quot; to get a list of all your activities
                    </li>
                    <li>Click on the desired activities</li>
                    <li>
                      Click on the tool icon (<IconGear />) on the top right
                    </li>
                    <li>Select &quot;Export as GPX file&quot;</li>
                  </InstructionList>
                  <InstructionNote>The activity is saved as a file on your pc.</InstructionNote>
                </>
              ),
            },
            {
              key: '1',
              label: <Text strong>Polar</Text>,
              children: (
                <>
                  <InstructionList>
                    <li>
                      Log in to{' '}
                      <a href="https://flow.polar.com" target="_blank" rel="noopener noreferrer">
                        Polar Flow
                      </a>{' '}
                      on your computer.
                    </li>
                    <li>
                      Click on the link &quot;Training History&quot; to get a list of all your
                      activities
                    </li>
                    <li>Click on the desired activities</li>
                    <li>Click on &quot;Export Session&quot;</li>
                    <li>Select &quot;Session (TCX)&quot;</li>
                  </InstructionList>
                  <InstructionNote>The activity is saved as a file on your pc.</InstructionNote>
                </>
              ),
            },
            {
              key: '2',
              label: <Text strong>Endomondo</Text>,
              children: (
                <>
                  <InstructionList>
                    <li>
                      Log in to{' '}
                      <a href="https://endomondo.com" target="_blank" rel="noopener noreferrer">
                        Endomondo
                      </a>{' '}
                      on your computer.
                    </li>
                    <li>Go to Training History to see your activities</li>
                    <li>Click on the desired activities</li>
                    <li>
                      Click on the arrow down (<IconArrowDown />) next to the title of your activity
                    </li>
                    <li>Select &quot;Export&quot;.</li>
                    <li>Select &quot;GPX format&quot;.</li>
                    <li>Click on the &quot;Export&quot; button</li>
                  </InstructionList>
                  <InstructionNote>The activity is saved as a file on your pc.</InstructionNote>
                </>
              ),
            },
            {
              key: '3',
              label: <Text strong>adidas Running by Runtastic</Text>,
              children: (
                <>
                  <InstructionList>
                    <li>
                      Log in to{' '}
                      <a
                        href="https://www.runtastic.com/de/login_ember"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        adidas Running by Runtastic
                      </a>{' '}
                      on your computer.
                    </li>
                    <li>Click on the desired activities</li>
                    <li>Click on the Settings icon next to the Edit button</li>
                    <li>Select &quot;Export as .gpx file&quot;</li>
                  </InstructionList>
                  <InstructionNote>The activity is saved as a file on your pc.</InstructionNote>
                </>
              ),
            },
            {
              key: '4',
              label: <Text strong>Strava</Text>,
              children: (
                <>
                  <InstructionList>
                    <li>
                      Log in to{' '}
                      <a href="https://strava.com" target="_blank" rel="noopener noreferrer">
                        Strava
                      </a>{' '}
                      on your computer.
                    </li>
                    <li>Go to your profile to see your activities</li>
                    <li>Click on the desired activities</li>
                    <li>
                      Click on the Settings icon (<IconSetting />) on the left
                    </li>
                    <li>Select &quot;Export GPX&quot;.</li>
                  </InstructionList>
                  <InstructionNote>The activity is saved as a file on your pc.</InstructionNote>
                </>
              ),
            },
            {
              key: '5',
              label: <Text strong>Under Armour</Text>,
              children: (
                <>
                  <InstructionList>
                    <li>
                      Log in to Under Armour on your computer through{' '}
                      <a href="https://mapmyrun.com" target="_blank" rel="noopener noreferrer">
                        MapMyRun
                      </a>
                      ,{' '}
                      <a href="https://mapmywalk.com" target="_blank" rel="noopener noreferrer">
                        MapMyWalk
                      </a>
                      ,{' '}
                      <a href="https://mapmyride.com" target="_blank" rel="noopener noreferrer">
                        MapMyRide
                      </a>{' '}
                      or{' '}
                      <a href="https://mapmyhike.com" target="_blank" rel="noopener noreferrer">
                        MapMyHike
                      </a>
                      .
                    </li>
                    <li>Go to your profile to see your activities</li>
                    <li>Click on the desired activities</li>
                    <li>Click on the TCX button</li>
                  </InstructionList>
                  <InstructionNote>The activity is saved as a file on your pc.</InstructionNote>
                </>
              ),
            },
          ]}
        />
        <VideoGuide>
          <Divider />
          <Title level={5}>Video guide</Title>
          <Paragraph type="secondary">
            Watch the walkthrough if you prefer seeing the export flow step by step.
          </Paragraph>
          <VideoFrame>
            <YoutubeIframe videoId="JloKXFsO5xM" />
          </VideoFrame>
        </VideoGuide>
      </HelperModal>
    );
  }

  const keyIsEmpty = ['/upload', '/upload/'].indexOf(location.pathname) !== -1;

  return (
    <UploadContainer>
      <UploadContent>
        <Title level={2}>Upload Track</Title>
        <Paragraph>
          Use your sports tracker such as Garmin, Polar or Suunto and upload recorded activities
          into a specific Tracking Map. Your uploaded geolocations data will be used for the
          activity comparison of many participants. For that reason, the geolocations are analyzed
          in a conservative manner. That means your geolocations might result in a bit less distance
          respectively a bit longer time in comparison to 3rd party applications.
          <HelperModal title={'Plausibility Check'}>
            <InstructionBulletList>
              <li>
                Unrealistic speed like 60 km/h for an ultra trail. Racemap ignores these
                geolocations. Practical relevance: if a participant forgets to stop tracking the
                activity while going by car, the car ride will be ignored automatically.
              </li>
              <li>
                Wrong timestamps: If uploaded geolocations are not within the start and end time of
                the event, the segment will be ignored.
              </li>
              <li>
                GPS sensitivity: Garmin, Strava, etc. provide their geolocation data with an
                accuracy of about 10%.
              </li>
            </InstructionBulletList>
          </HelperModal>
        </Paragraph>
        <Paragraph>
          Supported formats: GPX, KML and TCX.
          <UploadHelperModal />
        </Paragraph>
        <Paragraph>
          Enter the Passcode or a Tracking Key to select the correct Tracking map. Using the same
          Tracking Key you can upload multiple activities or segments into one Tracking Map.
        </Paragraph>

        <Form
          form={form}
          layout="vertical"
          style={{ width: '100%' }}
          autoComplete="off"
          onFinish={(values) => run(() => handleUpload(values))}
        >
          <Space orientation="vertical" style={{ width: '100%' }}>
            <Form.Item
              label="Key/Passcode:"
              name="key"
              validateDebounce={400}
              normalize={(value) => value?.toUpperCase()}
              hasFeedback
              rules={[
                { required: true, message: 'Please enter your key.' },
                { validator: (_rule, value) => (value ? validateKey(value) : Promise.resolve()) },
              ]}
            >
              <Input
                id="racemap_keylanding_key_input"
                placeholder="Enter your key here..."
                disabled={!keyIsEmpty || shouldOpenPlayer}
              />
            </Form.Item>

            <Form.Item label="Tracking Map:" name="eventName">
              <Input id="event_name_input" disabled readOnly />
            </Form.Item>

            <Form.Item
              label="Start Number:"
              name="startNumber"
              rules={[{ required: true, message: 'Please enter a start number.' }]}
            >
              <Input id="start_number_input" disabled={keyData.starterId != null} />
            </Form.Item>

            <Form.Item
              label="Name:"
              name="name"
              rules={[{ required: true, message: 'Please enter a name.' }]}
            >
              <Input id="starter_name_input" disabled={keyData.starterId != null} />
            </Form.Item>

            {track != null &&
              metaDataTrack != null &&
              track.points.length > 0 &&
              metaDataTrack.pointsOutsideEvent.length > 0 &&
              (track.points.length === metaDataTrack.pointsOutsideEvent.length ? (
                <Alert
                  showIcon
                  type="error"
                  title={
                    <>
                      All <b>{metaDataTrack.pointsOutsideEvent.length}</b> points of your uploaded
                      track are outside the start and end time of the Tracking Map.
                    </>
                  }
                />
              ) : (
                <Alert
                  showIcon
                  type="warning"
                  title={
                    <>
                      The uploaded track contains <b>{metaDataTrack.pointsOutsideEvent.length}</b>{' '}
                      points outside the start and end time of the Tracking Map.
                    </>
                  }
                />
              ))}

            <Form.Item
              label="Track:"
              name="track"
              getValueFromEvent={(newTrack: Track | null) => {
                if (newTrack == null || newTrack.points.length === 0) return null;
                return {
                  name: newTrack.name || `Track ${DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss')}`,
                  points: sortBy(newTrack.points, (p) => p.time.getTime()),
                };
              }}
              rules={[
                {
                  validator: (_rule, value: Track | null) =>
                    value != null && value.points.length > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error('Please upload a track.')),
                },
              ]}
            >
              <TrackDropView id="racemap_track_upload" value={null} onChange={() => {}} />
            </Form.Item>

            {eventErrorStatus === 404 && (
              <div id="racemap_keylanding_event_not_found">
                <Alert showIcon type="error" title="This Tracking Map does not exist." />
              </div>
            )}

            {eventErrorStatus === 403 && (
              <div id="racemap_keylanding_event_unavailable">
                <Alert
                  showIcon
                  type="error"
                  title="This Tracking Map is not available for uploads."
                />
              </div>
            )}

            {!dateCheck && (
              <div id="racemap_keylanding_event_unavailable">
                <Alert
                  showIcon
                  type="error"
                  title="Your event is over. You can't contribute your activity anymore."
                />
              </div>
            )}

            <ButtonRow>
              {shouldOpenPlayer ? (
                <Button
                  href={getLeaderboardUrl({ id: keyData.eventId } as RacemapEvent)}
                  icon={<IconExternal />}
                >
                  Open Leaderboard
                </Button>
              ) : (
                <Button
                  id="racemap_keylanding_button"
                  type="primary"
                  htmlType="submit"
                  loading={status === Tristate.SPINNING}
                  icon={status === Tristate.FAILURE ? <IconClose /> : undefined}
                  disabled={!dateCheck}
                >
                  Upload
                </Button>
              )}
            </ButtonRow>
          </Space>
        </Form>
      </UploadContent>
    </UploadContainer>
  );
}

const UploadContainer = styled.main`
  min-height: 100%;
  padding: 32px 16px 48px;
`;

const UploadContent = styled.div`
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
`;

const InstructionList = styled.ol`
  margin: 0;
  padding-left: 20px;

  li + li {
    margin-top: 6px;
  }
`;

const InstructionBulletList = styled.ul`
  margin: 0;
  padding-left: 20px;

  li + li {
    margin-top: 6px;
  }
`;

const InstructionNote = styled(Text)`
  display: block;
  margin-top: 12px;
`;

const VideoGuide = styled.section`
  margin-top: 24px;

  .ant-divider {
    margin: 0 0 20px;
  }

  .ant-typography {
    margin-bottom: 8px;
  }
`;

const VideoFrame = styled.div`
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 6px;

  iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 8px;
`;
